import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, User } from "lucide-react";
import { findPostBySlug, fmtPostDate } from "../data/blogPosts";

/** Set <title>, meta description, and Open Graph tags for the current post. */
function usePostSEO(post) {
  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | GenQuotes Blog`;

    const desc = (post.excerpt || "").slice(0, 150);
    const url = `https://genquotes.com/blog/${post.slug}`;

    const upsertMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", `${post.title} | GenQuotes Blog`);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:url", url);
    if (post.ogImage) upsertMeta("property", "og:image", post.ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", `${post.title} | GenQuotes Blog`);
    upsertMeta("name", "twitter:description", desc);
    if (post.ogImage) upsertMeta("name", "twitter:image", post.ogImage);
  }, [post]);
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPostBySlug(slug);
  usePostSEO(post);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="relative bg-[#0A1628] text-[#F8FAFC] py-16 sm:py-20 min-h-[70vh]">
      <div className="absolute inset-0 so-grid opacity-15 pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6">
        <Link
          to="/blog"
          data-testid="blog-post-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#06B6D4] transition-colors"
        >
          <ArrowLeft size={14} /> Back to blog
        </Link>

        <article data-testid={`blog-post-${post.slug}`} className="mt-8">
          <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
            Blog
          </div>
          <h1
            data-testid="blog-post-title"
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
          >
            {post.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] uppercase tracking-widest text-[#94A3B8] font-mono">
            <span className="inline-flex items-center gap-1.5">
              <User size={12} className="text-[#06B6D4]" /> {post.author}
            </span>
            <span>{fmtPostDate(post.publishedAt)}</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} className="text-[#06B6D4]" /> {post.readingTime}
            </span>
          </div>

          <div className="mt-10 space-y-6" data-testid="blog-post-body">
            {post.content.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={i}
                    className="mt-10 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#F8FAFC]"
                  >
                    {block.text}
                  </h2>
                );
              }
              return (
                <p
                  key={i}
                  className="text-[15px] sm:text-[17px] leading-[1.75] text-[#CBD5E1]"
                >
                  {block.text}
                </p>
              );
            })}
          </div>

          <hr className="mt-14 border-white/10" />
          <p
            data-testid="blog-post-share"
            className="mt-8 text-[15px] leading-relaxed text-[#94A3B8]"
          >
            Found this useful?{" "}
            <span className="text-[#F8FAFC]">
              Share it with a business owner who needs it.
            </span>
          </p>

          <Link
            to="/blog"
            className="mt-10 inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#06B6D4] transition-colors"
          >
            <ArrowLeft size={14} /> Back to blog
          </Link>
        </article>
      </div>
    </div>
  );
}
